// CyclesTab — Phase 5 Task 12 port of docs/mockups/leaderboard-detail.html
// states starting at L802 (default), L1059 (closed cycle), L1282 (archived),
// L1427 (empty), L1531 (Delete modal), L1730 (Adjust modal).
//
// Layout: 320px cycle list on the left + cycle panel on the right with TopTable
// and per-row score moderation (Delete / Adjust modals). The Reason textarea
// is intentionally NOT sent to the backend — the Go-truth `deleteScore` takes
// no body and `adjustScore` accepts only `{ score: number }`. A `reason` column
// on the audit row is planned as a Phase 5.3d backend extension. See the
// helper text below each Reason textarea.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Menu } from "../../../components/Menu/Menu";
import { Popover } from "../../../components/Popover/Popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Search, Trash2 } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Modal } from "../../../components/Modal/Modal";
import { Button } from "../../../components/Button/Button";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../../lib/errors";
import { formatRelativeShort } from "../../../lib/format";
import {
  adjustScore,
  deleteScore,
  getPlayerRoster,
  getRosterComposition,
  lbKeys,
  listCycleTop,
  listCycles,
  listRewards,
} from "../api";
import { listPlayers } from "../../players/api";
import {
  PLAYERS_PAGE_SIZE,
  playersPageQueryKey,
} from "../../players/queryKeys";
import { LAYER_META } from "../matchmaking";
import { isAutoScored } from "../autoScored";
import { cycleShape, describeSegments } from "../segments";
import type { SegmentDescriptor } from "../segments";
import { useBucketSummary } from "../useBucketSummary";
import { SegmentCell } from "../SegmentCell";
import { SegmentNote } from "../SegmentNote";
import { EntityCell } from "../EntityCell";
import { entityKindOf } from "../entityKind";
import type {
  Cycle,
  Entry,
  Leaderboard,
  MatchmakingLayerKind,
  Reward,
  RosterComposition,
  RosterEntry,
} from "../types";
import { fmtCycleRange } from "./cycleRange";
import styles from "./CyclesTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────────────

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function fmtNumber(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

function fmtAbs(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return ABS_FMT.format(d);
}

/** Compact humanised diff for the cphead "Closes in 3d 4h" line. */
function humaniseUntil(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const secs = Math.floor((d.getTime() - Date.now()) / 1000);
  if (secs <= 0) return "closed";
  const days = Math.floor(secs / 86400);
  const hrs = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

function shortId(id: string | undefined, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// ──────────────────────────────────────────────────────────────────────────────
// CyclesTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  gameId: string;
  lbId: string;
  /** Needed for its segmentation: this table spans every segment of the cycle. */
  lb: Leaderboard;
}

export function CyclesTab({ gameId, lbId, lb }: Props) {
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  // Memoised for its identity, not its cost: while the query has no data the
  // `?? []` fallback is a fresh array every render, and the hooks below depend
  // on this list.
  const cycles: Cycle[] = useMemo(
    () => cyclesQ.data?.cycles ?? [],
    [cyclesQ.data],
  );

  // Local selection. Until a cycle is picked by hand it is the one the link
  // named in `?cycle=`, else the first `open` one, else the first row —
  // computed here rather than written into state by an effect, so the list is
  // selected on the render the cycles arrive on.
  const [searchParams] = useSearchParams();
  const linkedCycleId = searchParams.get("cycle");
  const [pickedCycleId, setPickedCycleId] = useState<string>("");
  const selectedCycleId =
    pickedCycleId ||
    (cycles.find((c) => c.id === linkedCycleId)?.id ??
      cycles.find((c) => c.status === "open")?.id ??
      cycles[0]?.id ??
      "");

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === selectedCycleId),
    [cycles, selectedCycleId],
  );

  // === Loading / error / empty ====================================== //

  if (cyclesQ.isError) {
    return (
      <Alert variant="danger" title="Couldn't load cycles">
        {describeQueryError(cyclesQ.error)}
      </Alert>
    );
  }

  if (cyclesQ.isLoading) {
    return (
      <div className={styles.layout}>
        <aside className={styles.cycleList}>
          <Skeleton height={220} />
        </aside>
        <section className={styles.cyclePanel}>
          <Skeleton height={320} />
        </section>
      </div>
    );
  }

  if (cycles.length === 0) {
    // Mockup state at L1427 — brand-new leaderboard, no cycles yet.
    return (
      <div className={styles.emptyCard}>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <h3>No cycles yet</h3>
          {/* TODO(next-cycle-eta): if backend exposes a computed
              next_cycle_open_at on Leaderboard, render concrete date here
              (mockup shows "First cycle starts at Mon Jun 2 00:00 +03"). */}
          <p>
            When the first window opens, it will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <CycleListPane
        cycles={cycles}
        selectedId={selectedCycleId}
        onSelect={setPickedCycleId}
      />
      <CyclePanel
        gameId={gameId}
        lbId={lbId}
        lb={lb}
        cycle={selectedCycle}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Left pane — cycle list
// ──────────────────────────────────────────────────────────────────────────────

function CycleListPane({
  cycles,
  selectedId,
  onSelect,
}: {
  cycles: Cycle[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className={styles.cycleList}>
      <div className={styles.cycleListHead}>
        <span>Cycles</span>
        <span className={styles.cycleListCount}>{cycles.length}</span>
      </div>
      {cycles.map((c) => {
        const selected = c.id === selectedId;
        return (
          <button
            key={c.id}
            type="button"
            className={`${styles.cycleItem} ${selected ? styles.cycleItemSelected : ""}`}
            aria-pressed={selected}
            onClick={() => onSelect(c.id)}
          >
            <div className={styles.cycleRow}>
              <span className={styles.cid}>{c.id}</span>
              <CycleStatusPill status={c.status} />
            </div>
            <span className={styles.cycleRange}>{fmtCycleRange(c)}</span>
          </button>
        );
      })}
    </aside>
  );
}

function CycleStatusPill({ status }: { status: Cycle["status"] }) {
  const cls =
    status === "open"
      ? styles.cycleOpen
      : status === "closing"
        ? styles.cycleClosing
        : status === "scheduled"
          ? styles.cycleScheduled
          : status === "archived"
            ? styles.cycleArchived
            : styles.cycleClosed;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Right pane — selected cycle detail with TopTable
// ──────────────────────────────────────────────────────────────────────────────

function CyclePanel({
  gameId,
  lbId,
  lb,
  cycle,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  cycle: Cycle | undefined;
}) {
  // The board as THIS cycle's rows have to be read. Selecting an archived week
  // of rooms on a board that has since moved to personal lists must still draw
  // rooms: the Bucket column, the `412 buckets of 50` in the head and the note
  // under it all come from here, and off the board's current mode they would
  // silently vanish the moment the switch was made.
  const shape = cycleShape(lb, cycle);
  const summary = useBucketSummary(gameId, lbId, cycle?.id, shape);
  const segments = describeSegments(shape, summary);

  // A personal cycle has no combined top to ask for — its standings exist only
  // as ten-row lists, one per player — so the request is not merely hidden, it
  // is not made.
  const personal = cycle?.segmentation === "personal";
  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, cycle?.id ?? "", { limit: 50 }),
    queryFn: () => {
      if (!cycle) return Promise.reject(new Error("no cycle"));
      return listCycleTop(gameId, lbId, cycle.id, { limit: 50 });
    },
    enabled: !!cycle && !personal,
  });

  const entries: Entry[] = topQ.data?.entries ?? [];

  // === Modal state ================================================== //
  const [modal, setModal] = useState<
    | { kind: "delete"; entry: Entry }
    | { kind: "adjust"; entry: Entry }
    | null
  >(null);

  if (!cycle) {
    return (
      <section className={styles.cyclePanel}>
        <div className={styles.cyclePanelBody}>
          <div className={styles.placeholder}>Select a cycle.</div>
        </div>
      </section>
    );
  }

  // === cphead time-line — depends on cycle.status =================== //
  // open/closing → "Closes in 3d 4h"; scheduled → "Opens <abs>";
  // closed/archived → "Closed <abs>". Avoids the "Closes in closed" nonsense
  // that `humaniseUntil()` would otherwise produce on a past close_at.
  let timeLine: React.ReactNode = null;
  if (cycle.status === "open" || cycle.status === "closing") {
    timeLine = cycle.close_at && (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta} title={fmtAbs(cycle.close_at)}>
          Closes in {humaniseUntil(cycle.close_at)}
        </span>
      </>
    );
  } else if (cycle.status === "scheduled") {
    timeLine = (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>Opens {fmtAbs(cycle.open_at)}</span>
      </>
    );
  } else {
    // closed | archived
    timeLine = cycle.close_at && (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>Closed {fmtAbs(cycle.close_at)}</span>
      </>
    );
  }

  const panel = (
    <section className={styles.cyclePanel}>
      {/* TODO(cycle-rewards-status): add rewards-status chip ("Pending
          materialization" / "Materialized N grants") when Cycle DTO exposes
          materialized_count + status. Source-of-truth would be
          GET /reward_grants?cycle_id=… (Phase 5.2b). */}
      <div className={styles.cphead}>
        <span className={styles.cidPill}>{cycle.id}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.meta} title={fmtAbs(cycle.open_at)}>
          Opened {fmtAbs(cycle.open_at)}
        </span>
        {timeLine}
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>
          {fmtNumber(cycle.participants)} participants
        </span>
        {segments?.headLabel && (
          <>
            <span className={styles.sep}>·</span>
            <span className={styles.meta}>{segments.headLabel}</span>
          </>
        )}
        <CycleStatusPill status={cycle.status} />
      </div>

      {/* The CYCLE decides which body is drawn, never the board's current mode:
          a board that moved to personal lists last week still has weeks behind
          it that were played in rooms, and their score rows still name one.
          Reading the stamp is what keeps an archived cycle rendering as the
          thing it actually was. */}
      {personal ? (
        <PersonalCycleBody gameId={gameId} lbId={lbId} lb={lb} cycle={cycle} />
      ) : topQ.isError ? (
        <div className={styles.cyclePanelBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load top — {describeQueryError(topQ.error)}
          </div>
        </div>
      ) : topQ.isLoading ? (
        <div className={styles.cyclePanelBody}>
          <Skeleton height={220} />
        </div>
      ) : entries.length === 0 ? (
        cycle.status === "archived" ? (
          <ArchivedEmptyState />
        ) : (
          <div className={styles.cyclePanelBody}>
            <div className={styles.placeholder}>No entries in this cycle.</div>
          </div>
        )
      ) : (
        <>
          {segments && <SegmentNote parts={segments.cyclesNote} />}
          <TopTable
            gameId={gameId}
            lb={lb}
            entries={entries}
            segments={segments}
            onDelete={(entry) => setModal({ kind: "delete", entry })}
            onAdjust={(entry) => setModal({ kind: "adjust", entry })}
          />
        </>
      )}

      {modal?.kind === "delete" && (
        <DeleteScoreModal
          gameId={gameId}
          lbId={lbId}
          cycle={cycle}
          entry={modal.entry}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === "adjust" && (
        <AdjustScoreModal
          gameId={gameId}
          lbId={lbId}
          cycle={cycle}
          entry={modal.entry}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
  if (!personal) return panel;
  // Two panels on a personal cycle: the summary above, and the inspector — a
  // panel of its own because it answers about ONE player rather than about the
  // cycle, and the operator opens it with a name in hand.
  return (
    <div className={styles.personalStack}>
      {panel}
      <RosterInspector gameId={gameId} lbId={lbId} lb={lb} cycle={cycle} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Personal cycle — «how the lists were built» and the list inspector
// ──────────────────────────────────────────────────────────────────────────────

/**
 * The bar's palette, one colour per selection layer.
 *
 * Fixed per SOURCE rather than assigned by position: the same layer has to keep
 * the same colour between two cycles, or comparing last week's composition with
 * this week's means re-reading the legend. The first four are the mockup's own;
 * the two it does not draw take neighbouring hues that no pair of them can be
 * mistaken for.
 */
const SOURCE_COLOR: Record<MatchmakingLayerKind, string> = {
  friends: "#0d9488",
  guild: "#0891b2",
  similar_pace: "#3b82f6",
  recent_active: "#d97706",
  same_country: "#65a30d",
  bots: "#7e22ce",
};

/** The colour of a source, including one this build has never heard of.
 *
 * `source` is a string on the wire, not an enum, so a layer added on the server
 * before the panel is redeployed lands here — and without a colour its bar
 * segment and its legend swatch render with no background at all: a slice of
 * the picture that is simply invisible, in a chart whose whole job is to add up
 * to a hundred per cent. Grey is not the layer's colour; it is «this build does
 * not know this layer», which is exactly what the operator needs to see. */
const UNKNOWN_SOURCE_COLOR = "#94a3b8";

function sourceColor(source: string): string {
  return SOURCE_COLOR[source as MatchmakingLayerKind] ?? UNKNOWN_SOURCE_COLOR;
}

/** Where a source sorts. An unknown one goes LAST rather than first, which is
   what a bare indexOf's -1 would do — the bar is read left to right in the
   order the layers are spent, and a stranger belongs after the ones that are
   known, not ahead of the friends layer. */
function sourceRank(source: string): number {
  const i = SOURCE_ORDER.indexOf(source as MatchmakingLayerKind);
  return i < 0 ? SOURCE_ORDER.length : i;
}

/** The order the bar and the legend read in — the order the layers are
   consumed in, so the picture matches the rules that made it. */
const SOURCE_ORDER: MatchmakingLayerKind[] = [
  "friends",
  "guild",
  "similar_pace",
  "recent_active",
  "same_country",
  "bots",
];

/**
 * When the composition is worth an alert rather than a caption.
 *
 * Two fifths of every list being simulated is the point where the board stops
 * being a competition and starts being a score curve: the live layers have run
 * dry and the filler is carrying the cycle. Below it a bot share is ordinary —
 * it is what the last layer is FOR.
 */
const DEGRADED_BOT_SHARE = 0.4;

/** The podium tint of a rank, used by both tables — the combined one and the
   ten rows of a personal list, where a place still is a place. */
function rankClass(rank: number): string {
  return rank === 1
    ? styles.rankGold
    : rank === 2
      ? styles.rankSilver
      : rank === 3
        ? styles.rankBronze
        : "";
}

/** 1 → "1st". For the one sentence that names a place in words. */
function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** How many search results the inspector's menu shows. The REQUEST asks for a
   whole page of the player list, because that is what its cache key promises;
   this is only how far down the menu goes. */
const SEARCH_HITS = 8;

/**
 * The reward a place on a personal list actually wins, or null.
 *
 * Read off the board's own reward table rather than assumed: «top three get a
 * prize» is one board's arrangement, not a property of the mode, and this badge
 * is what an operator answers a support ticket from.
 *
 * `ranked` is how many rows the list actually ranks — the members that still
 * resolve to a player — because that is the set the payout's PERCENT_RANK runs
 * over, member for member (materializePersonalGrants). Tier rungs are skipped:
 * they are a ladder, not a placing on this list.
 */
function prizeFor(
  rewards: Reward[] | undefined,
  rank: number,
  ranked: number,
): string | null {
  if (!rewards || rank < 1) return null;
  const pctRank = ranked > 1 ? ((rank - 1) / (ranked - 1)) * 100 : 0;
  for (const r of rewards) {
    if (r.tier_key) continue;
    if (r.rank_from != null && r.rank_to != null) {
      if (rank >= r.rank_from && rank <= r.rank_to) return r.name;
      continue;
    }
    if (r.percentile_from != null && r.percentile_to != null) {
      if (pctRank >= r.percentile_from && pctRank <= r.percentile_to) {
        return r.name;
      }
    }
  }
  return null;
}

function pct(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

/** The share of lists carrying a heavy load of bots, to TWO significant
   figures — which is the rule the mockup prints in both of its states: `2.7% of
   lists` where the number is a sliver of a large denominator and the decimal
   carries information, `41% of lists` where it carries none. A fixed decimal
   would render the second as 41.3%. */
function fmtPct1(v: number): string {
  return v >= 10 ? `${Math.round(v)}%` : `${v.toFixed(1)}%`;
}

interface PersonalBodyProps {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  cycle: Cycle;
}

/**
 * The summary panel's body: four tiles and the composition bar.
 *
 * Everything here comes from the counters the assembly wrote, never from a scan
 * of the lists — ten million slots is not a question an admin screen may ask on
 * every open — which is also why there is no «average bots per list» anywhere:
 * the number that matters is the tail, and an average cannot show one.
 */
function PersonalCycleBody({ gameId, lbId, lb, cycle }: PersonalBodyProps) {
  const compQ = useQuery({
    queryKey: lbKeys.rosterComposition(gameId, lbId, cycle.id),
    queryFn: () => getRosterComposition(gameId, lbId, cycle.id),
    enabled: !!gameId && !!lbId && !!cycle.id,
  });

  if (compQ.isError) {
    return (
      <div className={styles.cyclePanelBody}>
        <div className={styles.cardError}>
          Couldn&apos;t load the composition — {describeQueryError(compQ.error)}
        </div>
      </div>
    );
  }
  if (compQ.isLoading || !compQ.data) {
    return (
      <div className={styles.cyclePanelBody}>
        <Skeleton height={180} />
      </div>
    );
  }
  return (
    <div className={styles.cyclePanelBody}>
      <Composition comp={compQ.data} lb={lb} cycle={cycle} />
    </div>
  );
}

function Composition({
  comp,
  lb,
  cycle,
}: {
  comp: RosterComposition;
  lb: Leaderboard;
  cycle: Cycle;
}) {
  const sources = useMemo(
    () =>
      [...(comp.sources ?? [])].sort(
        (a, b) => sourceRank(a.source) - sourceRank(b.source),
      ),
    [comp.sources],
  );
  const bots = sources.find((s) => s.source === "bots")?.slots ?? 0;
  const live = comp.opponent_slots - bots;
  // How many opponents one list holds — the owner's own slot is not one of
  // them. Taken from the CYCLE's stamp rather than from the board: the length
  // is frozen with the cycle, so a roster_size changed since then describes a
  // week this panel is not showing.
  //
  // A personal cycle always carries one (lbc_roster_size_check), so the fallback
  // below is for a payload from an older server. It divides the slots by the
  // lists rather than assuming a length: guessing would put a number under
  // «Lists with N+ bots» that names a threshold nobody configured.
  const perList =
    cycle.roster_size != null
      ? cycle.roster_size - 1
      : comp.lists > 0
        ? Math.round(comp.opponent_slots / comp.lists)
        : 0;
  // «Half the opponents or more», which on a list of ten is the mockup's own
  // 5+. Derived rather than fixed at five, because a board of six lists would
  // otherwise have a tile that can never light up.
  const heavyFrom = Math.max(1, Math.ceil(perList / 2));
  const heavyLists = (comp.bot_lists ?? [])
    .filter((b) => b.bots >= heavyFrom)
    .reduce((n, b) => n + b.lists, 0);
  const degraded =
    comp.opponent_slots > 0 && bots / comp.opponent_slots >= DEGRADED_BOT_SHARE;
  // The live layers named in the alert, so the sentence points at the rules that
  // ran dry rather than at a generic list — and ONLY on the cycle those rules
  // actually built. `matchmaking_config` is the board's rules as of NOW; on a
  // finished week they may be nothing like the ones that assembled it, and an
  // operator answering «why did I get only bots last week» would be reading an
  // explanation of a different configuration. On such a cycle the alert states
  // the fact and stops naming causes it cannot know.
  const liveNow = cycle.status === "open" || cycle.status === "closing";
  const liveLayers = liveNow
    ? (lb.matchmaking_config?.layers ?? [])
        .map((l) => l.kind)
        .filter((k) => k !== "bots")
    : [];

  return (
    <>
      <div className={styles.heroStats}>
        <div>
          <div className={styles.heroLabel}>Lists assembled</div>
          <div className={styles.heroValue}>{fmtNumber(comp.lists)}</div>
          {/* No «+62 since yesterday» line: the counters are running totals,
              not a series, and a delta invented from one reading would be a
              number nobody could check. */}
        </div>
        <div>
          <div className={styles.heroLabel}>Opponent slots</div>
          <div className={styles.heroValue}>
            {fmtNumber(comp.opponent_slots)}
          </div>
          {perList > 0 && (
            <div className={styles.heroSub}>{perList} per list</div>
          )}
        </div>
        <div>
          <div className={styles.heroLabel}>Live opponents</div>
          <div className={styles.heroValue}>
            {Math.round(pct(live, comp.opponent_slots))}%
          </div>
          <div className={styles.heroSub}>
            {fmtNumber(live)} of {fmtNumber(comp.opponent_slots)}
          </div>
        </div>
        <div>
          <div className={styles.heroLabel}>
            Lists with {heavyFrom}+ bots
          </div>
          <div className={styles.heroValue}>{fmtNumber(heavyLists)}</div>
          <div className={styles.heroSub}>
            {fmtPct1(pct(heavyLists, comp.lists))} of lists
          </div>
        </div>
      </div>

      <div className={styles.comp}>
        <div className={styles.compHead}>
          <span className={styles.compTitle}>How the lists were built</span>
          <span className={styles.compSub}>
            {fmtNumber(comp.opponent_slots)} opponent slots across{" "}
            {fmtNumber(comp.lists)} lists
          </span>
        </div>

        {degraded && (
          <Alert
            variant="warn"
            title="Almost half of every list is simulated"
          >
            {liveLayers.length > 0 ? (
              <>
                Live candidates are running out: the{" "}
                {liveLayers.map((k, i) => (
                  <span key={k}>
                    {i > 0
                      ? i === liveLayers.length - 1
                        ? " and "
                        : ", "
                      : null}
                    <code>{k}</code>
                  </span>
                ))}{" "}
                {liveLayers.length === 1 ? "layer is" : "layers are"} exhausted
                and bots take the remainder. Loosen <code>freshness_days</code>{" "}
                or widen the <code>similar_pace</code> band — otherwise players
                are competing against the simulation.
              </>
            ) : (
              <>
                Live candidates ran out while this cycle was being assembled and
                bots took the remainder. The board&apos;s matchmaking rules may
                have changed since, so they are not named here — this is what
                the lists of this cycle were actually built from.
              </>
            )}
          </Alert>
        )}

        <div className={styles.compBar}>
          {sources.map((s) => {
            const share = pct(s.slots, comp.opponent_slots);
            return (
              <div
                key={s.source}
                className={`${styles.compSeg} ${
                  share < 6 ? styles.compSegNarrow : ""
                }`}
                style={{
                  width: `${share}%`,
                  background: sourceColor(s.source),
                }}
                title={`${LAYER_META[s.source]?.title ?? s.source}: ${fmtNumber(
                  s.slots,
                )}`}
              >
                {Math.round(share)}%
              </div>
            );
          })}
        </div>

        <div className={styles.legend}>
          {sources.map((s) => (
            <div key={s.source} className={styles.legendItem}>
              <span
                className={styles.swatch}
                style={{ background: sourceColor(s.source) }}
                aria-hidden="true"
              />
              <b>{LAYER_META[s.source]?.title ?? s.source}</b>{" "}
              <code>{s.source}</code>{" "}
              <span className={styles.legendCount}>{fmtNumber(s.slots)}</span> ·{" "}
              {Math.round(pct(s.slots, comp.opponent_slots))}%
            </div>
          ))}
          {sources.length === 0 && (
            <span className={styles.compSub}>
              No lists assembled yet — the first player to open the board builds
              theirs.
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * «Show me THIS player's list».
 *
 * The operator arrives from a support ticket with a name, and the roster
 * endpoint is a point lookup by player id — a cycle holds one list per player
 * who played it, so there is no listing of rosters to filter. The search box
 * bridges the two, against the project's own player list.
 */
function RosterInspector({ gameId, lbId, lb, cycle }: PersonalBodyProps) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [playerId, setPlayerId] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // A keystroke is not a question. The list endpoint is a search over the
  // project's players, so it is asked once the typing stops rather than per
  // letter.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // The PROJECT's player list, on the project's own query key — the same
  // request and the same cache entry the Players page fills. A second search
  // endpoint beside it would be two states of one answer, and the page size has
  // to match for the shared key to mean what it says.
  const hitsQ = useQuery({
    queryKey: playersPageQueryKey(gameId, debounced, 1, false, false),
    queryFn: () =>
      listPlayers(gameId, { q: debounced, page: 1, limit: PLAYERS_PAGE_SIZE }),
    enabled: !!gameId && debounced.length >= 2 && open,
  });

  const rosterQ = useQuery({
    queryKey: lbKeys.playerRoster(gameId, lbId, cycle.id, playerId),
    queryFn: () => getPlayerRoster(gameId, lbId, cycle.id, playerId),
    enabled: !!playerId,
  });

  // The board's own rewards, so the badge below can say whether this place is
  // actually paid on THIS board instead of assuming a podium of three.
  const rewardsQ = useQuery({
    queryKey: lbKeys.rewards(gameId, lbId),
    queryFn: () => listRewards(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  const hits = (hitsQ.data?.items ?? []).slice(0, SEARCH_HITS);
  const roster = rosterQ.data;
  const members: RosterEntry[] = roster?.members ?? [];
  const owner = members.find((m) => m.player.id === roster?.owner_id);
  // The gate as the board states it TODAY, which is only the gate this cycle was
  // assembled under while the cycle is still the running one. On a finished week
  // a threshold since changed would be printed beside lists it never applied to.
  const liveNow = cycle.status === "open" || cycle.status === "closing";
  const gate = liveNow ? lb.matchmaking_config?.entry_gate : undefined;
  // The length the list was CUT to. `members` is what still renders, which is
  // one row short for every member deleted since — an honest number for the
  // table, and the wrong denominator for «rank 2 of 10», which is the sentence
  // the player's own screen shows them.
  const listSize = cycle.roster_size ?? members.length;
  const prize = roster
    ? prizeFor(rewardsQ.data, roster.owner_rank, members.length)
    : null;

  const close = useCallback(() => setOpen(false), []);
  const pick = (id: string, name: string) => {
    setPlayerId(id);
    setQuery(name);
    setOpen(false);
  };
  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (hits.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + hits.length) % hits.length);
      return;
    }
    if (e.key === "Enter" && open && hits[active]) {
      e.preventDefault();
      pick(hits[active].id, hits[active].display_name);
    }
  };

  return (
    <section className={styles.cyclePanel}>
      <div className={styles.cphead}>
        <span className={styles.compTitle}>Player&apos;s list</span>
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>open one player&apos;s list</span>
        <div className={styles.searchWrap} ref={boxRef}>
          <span className={styles.searchIcon} aria-hidden="true">
            <Search size={14} />
          </span>
          {/* A combobox rather than a text field with a panel beside it: the
              menu is the only way to reach a player from here, so a keyboard
              that cannot walk it cannot open a list at all. */}
          <input
            className={styles.searchInput}
            placeholder="Find a player…"
            aria-label="Find a player"
            role="combobox"
            aria-expanded={open && debounced.length >= 2}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && hits[active] ? `${listId}-${active}` : undefined
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onSearchKey}
          />
          {/* In a portal, not under the box: the card around it clips its own
              overflow for the rounded corners, and a menu of five hits is
              taller than the header it hangs from. The popover also flips the
              list above the field when the window's bottom edge is close.

              The combobox above survives the move because `aria-controls` is
              what ties it to the list: ARIA lets an active descendant live
              inside the controlled element, wherever that element sits. */}
          <Popover
            open={open && debounced.length >= 2}
            onClose={close}
            anchorRef={boxRef}
            placement="bottom-end"
            // As wide as `.searchInput`, which the menu lines up under.
            width={268}
            // Four, not the default six: the gap stays what it was under the
            // 32px-tall field.
            offset={4}
          >
            <div className={styles.searchMenu} role="listbox" id={listId}>
              {hits.length === 0 ? (
                <div className={styles.searchEmpty}>
                  {hitsQ.isLoading ? "Searching…" : "No player found."}
                </div>
              ) : (
                hits.map((h, i) => (
                  <div
                    key={h.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className={`${styles.searchHit} ${
                      i === active ? styles.searchHitActive : ""
                    }`}
                    // mousedown, not click: the input loses focus first
                    // otherwise, and the blur closes the menu out from under
                    // the pointer.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(h.id, h.display_name);
                    }}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span className={styles.searchHitName}>
                      {h.display_name}
                    </span>
                    <span className={styles.searchHitId}>{h.external_id}</span>
                  </div>
                ))
              )}
            </div>
          </Popover>
        </div>
      </div>

      {!playerId ? (
        <div className={styles.cyclePanelBody}>
          <div className={styles.placeholder}>
            Find a player to see the list they were given this cycle.
          </div>
        </div>
      ) : rosterQ.isError ? (
        <div className={styles.cyclePanelBody}>
          <div className={styles.placeholder}>
            {/* A 404 here is the ordinary answer, not a failure: this player
                never touched the board in this cycle, so no list was ever
                assembled — and opening one must not create it. */}
            {rosterQ.error instanceof ApiError &&
            rosterQ.error.code === "leaderboard_not_found"
              ? "No list — this player has not opened the board in this cycle."
              : describeQueryError(rosterQ.error)}
          </div>
        </div>
      ) : rosterQ.isLoading || !roster ? (
        <div className={styles.cyclePanelBody}>
          <Skeleton height={220} />
        </div>
      ) : (
        <>
          <div className={styles.inspHead}>
            {owner && (
              <EntityCell
                gameId={gameId}
                id={owner.player.id}
                name={owner.player.display_name}
                kind="player"
                size="sm"
              />
            )}
            <span className={styles.sep}>·</span>
            <span title={roster.assembled_at}>
              assembled {fmtAbs(roster.assembled_at)}
            </span>
            {gate && (
              <>
                <span className={styles.sep}>·</span>
                {/* The requirement, not the player's own value: the roster read
                    answers about a LIST, and asking a second endpoint for one
                    number would be a request per opened list. */}
                <span>
                  gate{" "}
                  <code>
                    {gate.key} ≥ {gate.min}
                  </code>
                </span>
              </>
            )}
            {/* «of ten», not «of nine»: the list was cut to the cycle's own
                roster_size, and that is the number the player is shown. A
                member deleted since simply has no row in the table below, which
                the note under it says in words.

                The prize half is read off the board's REWARD TABLE rather than
                assumed to be a podium of three: a board may pay one place, or
                pay by percentile, and this is the screen an operator answers a
                «where is my prize» ticket from. */}
            <span className={styles.rankBadge} title={prize ?? undefined}>
              rank {roster.owner_rank} of {listSize}
              {prize ? ` → prize for ${ordinal(roster.owner_rank)} place` : ""}
            </span>
            <Link
              className={styles.inspLink}
              to={`/projects/${encodeURIComponent(gameId)}/players/${encodeURIComponent(
                roster.owner_id,
              )}`}
            >
              Open player card →
            </Link>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 56 }}>Rank</th>
                <th>Player</th>
                <th className={styles.num} style={{ width: 96 }}>
                  Score
                </th>
                <th style={{ width: 168 }}>Why in the list</th>
                <th style={{ width: 152 }}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.player.id} className={m.is_me ? styles.selfRow : ""}>
                  <td className={`${styles.rank} ${rankClass(m.rank)}`}>
                    {m.rank}
                  </td>
                  <td>
                    <EntityCell
                      gameId={gameId}
                      id={m.player.id}
                      name={m.player.display_name}
                      kind="player"
                    />
                  </td>
                  <td className={`${styles.num} ${styles.score}`}>
                    {fmtNumber(m.score)}
                  </td>
                  <td>
                    <SourceCell source={m.source} />
                  </td>
                  <td className={styles.muted} title={m.last_seen_at ?? ""}>
                    {m.last_seen_at ? formatRelativeShort(m.last_seen_at) : "never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.inspNote}>
            The line-up is frozen for the whole cycle: each slot&apos;s{" "}
            <code>source</code> is recorded at assembly time. A deleted player is
            simply not rendered — the list shows nine rows instead of ten.
          </div>
        </>
      )}
    </section>
  );
}

/** Which layer put this row on the list, as a dot and a word. */
function SourceCell({ source }: { source: RosterEntry["source"] }) {
  if (source === "self") {
    return (
      <span className={`${styles.src} ${styles.srcSelf}`}>
        <span className={styles.srcSwatch} aria-hidden="true" />
        This player
      </span>
    );
  }
  return (
    <span className={styles.src}>
      <span
        className={styles.srcSwatch}
        style={{ background: SOURCE_COLOR[source] }}
        aria-hidden="true"
      />
      {LAYER_META[source]?.title ?? source}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// TopTable with per-row moderation kebab menu
// ──────────────────────────────────────────────────────────────────────────────

function TopTable({
  gameId,
  lb,
  entries,
  segments,
  onDelete,
  onAdjust,
}: {
  gameId: string;
  lb: Leaderboard;
  entries: Entry[];
  segments: SegmentDescriptor | null;
  onDelete: (e: Entry) => void;
  onAdjust: (e: Entry) => void;
}) {
  // Every score here was computed by the server from a players.stats key, and
  // both moderation calls — DELETE and PATCH on a cycle's score — answer 403
  // leaderboard_auto_scored. So the menu is taken away rather than offered and
  // then explained by a refusal: the same rule the config form applies to
  // `operator`, submit_policy and the guards, which it removes rather than
  // greys out. The column goes with it, or every row keeps a cell holding
  // nothing.
  const moderatable = !isAutoScored(lb);
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: 56 }}>Rank</th>
          <th>Entity</th>
          {segments && <th>{segments.columnLabel}</th>}
          <th className={styles.num}>Score</th>
          <th style={{ width: 120 }}>Updated at</th>
          {moderatable && <th style={{ width: 40 }} aria-hidden="true" />}
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <TopRow
            key={e.player.id}
            gameId={gameId}
            lb={lb}
            entry={e}
            segments={segments}
            moderatable={moderatable}
            onDelete={() => onDelete(e)}
            onAdjust={() => onAdjust(e)}
          />
        ))}
      </tbody>
    </table>
  );
}

function TopRow({
  gameId,
  lb,
  entry,
  segments,
  moderatable,
  onDelete,
  onAdjust,
}: {
  gameId: string;
  lb: Leaderboard;
  entry: Entry;
  segments: SegmentDescriptor | null;
  /** False on a board whose scores the server writes itself — see TopTable. */
  moderatable: boolean;
  onDelete: () => void;
  onAdjust: () => void;
}) {
  return (
    <tr>
      <td className={`${styles.rank} ${rankClass(entry.rank)}`}>{entry.rank}</td>
      <td>
        {/* TODO(player-handle): expose handle on EntryPlayer once
            /v1/leaderboards/{lb}/top backend supports ?expand=handle.
            Mockup shows "Alice @alice" — handle is not in Entry DTO yet. */}
        <EntityCell
          gameId={gameId}
          id={entry.player.id}
          name={entry.player.display_name}
          kind={entityKindOf(lb)}
        />
      </td>
      {segments && <SegmentCell entry={entry} kind={segments.kind} />}
      <td className={`${styles.num} ${styles.score}`}>{fmtNumber(entry.score)}</td>
      <td className={styles.muted} title={entry.updated_at}>
        {formatRelativeShort(entry.updated_at)}
      </td>
      {moderatable && (
        <td className={styles.actionCell}>
          <RowMenu onDelete={onDelete} onAdjust={onAdjust} />
        </td>
      )}
    </tr>
  );
}

function RowMenu({
  onDelete,
  onAdjust,
}: {
  onDelete: () => void;
  onAdjust: () => void;
}) {
  return (
    <Menu
      aria-label="Score actions"
      placement="bottom-end"
      items={[
        {
          label: "Delete score",
          icon: <Trash2 size={14} aria-hidden="true" />,
          variant: "danger",
          onSelect: onDelete,
        },
        {
          label: "Adjust score…",
          icon: <Edit3 size={14} aria-hidden="true" />,
          onSelect: onAdjust,
        },
      ]}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Archived empty-state (mockup L1411–1417)
// ──────────────────────────────────────────────────────────────────────────────

function ArchivedEmptyState() {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="21 8 21 21 3 21 3 8" />
          <rect x="1" y="3" width="22" height="5" />
          <line x1="10" y1="12" x2="14" y2="12" />
        </svg>
      </span>
      <h3>Top data archived</h3>
      <p>
        Redis cache for this cycle was evicted. Aggregate counts and audit are
        preserved in Postgres.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Modals
// ──────────────────────────────────────────────────────────────────────────────

const REASON_HELPER =
  "Reason is collected here for operator-side workflow records. Backend persistence (a reason column on the audit row) is planned as a Phase 5.3d backend extension; until then this field is informational only.";

/** Invalidate the queries that depend on the just-mutated cycle. */
function useInvalidateCycle(gameId: string, lbId: string, cycleId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({
      queryKey: ["leaderboard.top", gameId, lbId, cycleId],
    });
    void qc.invalidateQueries({ queryKey: lbKeys.cycle(gameId, lbId, cycleId) });
    void qc.invalidateQueries({
      // Match every audit filter under this cycle without forcing callers to
      // know the exact key shape (which embeds the AuditListFilter).
      predicate: (q) =>
        q.queryKey[0] === "leaderboard.audit" &&
        q.queryKey[1] === gameId &&
        q.queryKey[2] === lbId &&
        q.queryKey[3] === cycleId,
    });
  };
}

function DeleteScoreModal({
  gameId,
  lbId,
  cycle,
  entry,
  onClose,
}: {
  gameId: string;
  lbId: string;
  cycle: Cycle;
  entry: Entry;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateCycle(gameId, lbId, cycle.id);

  const mutation = useMutation({
    mutationFn: ({ entityId }: { entityId: string }) =>
      deleteScore(gameId, lbId, cycle.id, entityId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete score?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate({ entityId: entry.player.id })}
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            Delete
          </Button>
        </>
      }
    >
      <div className={styles.modalIconRow}>
        <span className={`${styles.modalIcon} ${styles.modalIconDanger}`} aria-hidden="true">
          <Trash2 size={16} />
        </span>
      </div>

      <p className={styles.modalBodyText}>
        This will remove <b>{entry.player.display_name}</b>{" "}
        (player_id: <code>{shortId(entry.player.id)}-…</code>)&apos;s score from{" "}
        <code>{cycle.id}</code>. The action is logged to the audit channel as{" "}
        <code>admin</code>.
      </p>

      <label className={styles.modalLabel} htmlFor="delete-reason">
        Reason
      </label>
      <textarea
        id="delete-reason"
        className={styles.textarea}
        placeholder="e.g., Manual rollback after exploit report #2841…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
      />
      <span className={styles.helper}>{REASON_HELPER}</span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}

function AdjustScoreModal({
  gameId,
  lbId,
  cycle,
  entry,
  onClose,
}: {
  gameId: string;
  lbId: string;
  cycle: Cycle;
  entry: Entry;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [newScore, setNewScore] = useState<string>(String(entry.score));
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateCycle(gameId, lbId, cycle.id);

  const newScoreId = "adjust-new-score";

  const mutation = useMutation({
    mutationFn: ({ entityId, score }: { entityId: string; score: number }) =>
      adjustScore(gameId, lbId, cycle.id, entityId, { score }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  // Validation: finite integer (positive or zero). NaN/empty blocks submit.
  const parsed = Number(newScore);
  const scoreValid =
    newScore.trim().length > 0 &&
    Number.isFinite(parsed) &&
    Number.isInteger(parsed);
  const canSubmit = scoreValid && reason.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust score"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              mutation.mutate({ entityId: entry.player.id, score: parsed })
            }
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className={styles.modalIconRow}>
        <span className={`${styles.modalIcon} ${styles.modalIconAccent}`} aria-hidden="true">
          <Edit3 size={16} />
        </span>
      </div>

      <p className={styles.modalBodyText}>
        Set a new score for <b>{entry.player.display_name}</b>{" "}
        (player_id: <code>{shortId(entry.player.id)}-…</code>) in{" "}
        <code>{cycle.id}</code>. The audit log will record both the old and new
        values.
      </p>

      <div className={styles.modalGrid2}>
        <div className={styles.modalField}>
          <label className={styles.modalLabel}>Old score</label>
          <span className={styles.roPill}>{fmtNumber(entry.score)}</span>
        </div>
        <div className={styles.modalField}>
          <label className={styles.modalLabel} htmlFor={newScoreId}>
            New score
          </label>
          <input
            id={newScoreId}
            type="number"
            inputMode="numeric"
            className={`${styles.input} ${styles.inputMono}`}
            value={newScore}
            onChange={(e) => setNewScore(e.target.value)}
          />
        </div>
      </div>

      <label className={styles.modalLabel} htmlFor="adjust-reason">
        Reason
      </label>
      <textarea
        id="adjust-reason"
        className={styles.textarea}
        placeholder="e.g., Reverted +340 boost from disputed match #5512…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
      />
      <span className={styles.helper}>{REASON_HELPER}</span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
